import type { SubscriptionTier } from "@/shared/lib/supabase/types"

// ── Coin Costs ──────────────────────────────────────────────────────────────
// Re-exported from shared constants (canonical source of truth).
export { COIN_COSTS, isPaidTier } from "@/shared/lib/constants"
export type { CoinActionType } from "@/shared/lib/constants"

// ── Annual discount (display + "Save X%" badge) ──────────────────────────────
// Stripe has separate monthly/yearly price IDs; this drives UI math only.
export const ANNUAL_DISCOUNT_PERCENT = 60

/** Monthly-equivalent price when billed annually. Use for display and "Save X%" math. */
export function getYearlyMonthlyPrice(monthlyPrice: number): number {
  if (monthlyPrice === 0) return 0
  const multiplier = 1 - ANNUAL_DISCOUNT_PERCENT / 100
  return Math.round(monthlyPrice * multiplier * 100) / 100
}

function yearlyMonthlyFromPrice(monthlyPrice: number): number {
  return getYearlyMonthlyPrice(monthlyPrice)
}

// ── Tier Configuration ──────────────────────────────────────────────────────

export interface TierConfig {
  monthlyCoins: number
  messagesPerDay: number  // Infinity for unlimited
  freeImagesPerMonth: number
  hasVoice: boolean
  hasPhoneCall: boolean
  price: number           // Monthly price in USD
  yearlyPrice: number     // Monthly equivalent when billed annually (from ANNUAL_DISCOUNT_PERCENT)
}

export const TIER_CONFIG: Record<SubscriptionTier, TierConfig> = {
  free: {
    monthlyCoins: 0,
    messagesPerDay: 5,
    freeImagesPerMonth: 2,
    hasVoice: false,
    hasPhoneCall: false,
    price: 0,
    yearlyPrice: 0,
  },
  starter: {
    monthlyCoins: 1500,
    messagesPerDay: Infinity,
    freeImagesPerMonth: 0,
    hasVoice: true,
    hasPhoneCall: true,
    price: 12.99,
    yearlyPrice: yearlyMonthlyFromPrice(12.99),
  },
  premium: {
    monthlyCoins: 5000,
    messagesPerDay: Infinity,
    freeImagesPerMonth: 0,
    hasVoice: true,
    hasPhoneCall: true,
    price: 27.99,
    yearlyPrice: yearlyMonthlyFromPrice(27.99),
  },
  ultimate: {
    monthlyCoins: 10000,
    messagesPerDay: Infinity,
    freeImagesPerMonth: 0,
    hasVoice: true,
    hasPhoneCall: true,
    price: 49.99,
    yearlyPrice: yearlyMonthlyFromPrice(49.99),
  },
} as const

// ── Free User Limits ────────────────────────────────────────────────────────

export const FREE_LIMITS = {
  messagesPerDay: 5,
  imagesPerMonth: 2,
  maxImagesPerGeneration: 2,
  charactersPerMonth: 1,
} as const

// ── Paid Tiers (helpers) ────────────────────────────────────────────────────

export function tierHasFeature(
  tier: SubscriptionTier,
  feature: "voice" | "phone_call"
): boolean {
  const config = TIER_CONFIG[tier]
  if (feature === "voice") return config.hasVoice
  if (feature === "phone_call") return config.hasPhoneCall
  return false
}

// ── Coin Balance Thresholds ─────────────────────────────────────────────────
// Define thresholds for different coin balance states
export const COIN_BALANCE_THRESHOLDS = {
  EMPTY: 0,
  CRITICAL: 10,
  LOW: 50,
} as const

export type CoinBalanceState = "empty" | "critical" | "low" | "sufficient"

// ── Low Balance Threshold ───────────────────────────────────────────────────
// If user's balance drops below this, show a warning in the topbar.
export const LOW_BALANCE_THRESHOLD = 50

// ── Tier Ordering (for upgrade / downgrade logic) ──────────────────────────

export const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 1,
  premium: 2,
  ultimate: 3,
}

export function getTierDirection(
  current: SubscriptionTier,
  target: SubscriptionTier
): "upgrade" | "downgrade" | "same" {
  if (TIER_ORDER[target] > TIER_ORDER[current]) return "upgrade"
  if (TIER_ORDER[target] < TIER_ORDER[current]) return "downgrade"
  return "same"
}
